import { useCallback, useMemo, useState } from "react"
import { createPortal } from "react-dom"
import { format } from "date-fns"
import { Check, ChevronDown, Loader2, RefreshCw, UserRound } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { DateRangePicker } from "@/components/ui/DateRangePicker"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useAppDispatch, useAppSelector } from "@/reducers/hooks"
import { changeActiveTab, TAB_TYPES, TabType } from "@/reducers/tabsSlice"
import { changeActiveChat } from "@/reducers/chatsSlice"
import { driverJid as toDriverBareJid } from "@/jidUtils"
import { LicensePlate } from "@/components/ui/license-plate"
import {
  getPlatesClaimedByChats,
  resolveDriverPlate,
} from "@/reducers/accountChatsSelectors"
import { resolveDriverDisplayName } from "@/components/cmrReport/resolveDriverDisplayName"
import { formatJidLocalpartAsName } from "@/components/cmrReport/resolveSearchDriverName"
import { cn } from "@/lib/utils"
import { useFuelReportData, type FuelRange } from "./useFuelReportData"
import { FuelSummaryCard } from "./FuelSummaryCard"
import { FuelTimelineChip } from "./FuelTimelineChip"
import { FuelEventDetailDialog } from "./FuelEventDetailDialog"
import {
  computeFuelSummary,
  driverChipEvents,
  driverStats,
  eventDayWindow,
  formatPct,
  type ChipEvent,
} from "./fuelReportSummary"
import type { DriverFuelRow } from "./fuelReportTypes"
import { useTranslation } from "react-i18next"

type ResolvedDriver = { name: string; chatJid?: string; plate?: string }

/**
 * Resolve a human name + navigable chat for each driver row. The backend
 * sends `driverJid` in the `localpart@drivers.<vhost>` (MUC) form and
 * `chatJid` is often null, so we: normalize the JID to the bare user form,
 * find the matching chat (via account config / the chat list), and resolve
 * the configured chat name, falling back to the driver's profile name, then
 * a title-cased localpart — never the raw JID.
 */
function useDriverNameMap(
  drivers: DriverFuelRow[],
): Map<string, ResolvedDriver> {
  const chats = useAppSelector((s) => s.chats)
  const profiles = useAppSelector((s) => s.profiles)
  const accountChats = useAppSelector((s) => s.accountChats)
  const cars = useAppSelector((s) => s.cars)

  return useMemo(() => {
    const safeChats = chats ?? []
    const safeProfiles = profiles ?? []
    const safeAccountChats = accountChats ?? {}
    const safeCars = cars ?? {}
    // Computed once for the whole table, not per row — it walks every chat.
    const platesClaimedByChats = getPlatesClaimedByChats(
      safeAccountChats,
      safeCars,
    )

    // Map each chat's derived driver-bare JID → its chat JID.
    const chatByDriver: Record<string, string> = {}
    for (const chat of safeChats) {
      try {
        chatByDriver[toDriverBareJid(chat.jid)] = chat.jid
      } catch {
        // unparseable chat jid — skip
      }
    }

    const map = new Map<string, ResolvedDriver>()
    for (const d of drivers) {
      let bare = d.driverJid
      try {
        bare = toDriverBareJid(d.driverJid)
      } catch {
        // keep the raw jid
      }
      const chatJid = d.chatJid ?? chatByDriver[bare]
      const resolved = resolveDriverDisplayName({
        chatJid,
        driverJid: bare,
        chats: safeChats,
        profiles: safeProfiles,
        accountChats: safeAccountChats,
      })
      const unresolved = resolved === bare || resolved === d.driverJid
      let name = resolved
      if (unresolved) {
        const profile = safeProfiles.find((p) => p.jid === bare)
        name =
          (profile?.firstName && profile?.lastName
            ? `${profile.firstName} ${profile.lastName}`
            : undefined) ??
          formatJidLocalpartAsName(d.driverJid) ??
          d.driverJid
      }
      const plate = resolveDriverPlate({
        accountChat: chatJid ? safeAccountChats[chatJid] : undefined,
        cars: safeCars,
        driverProfile: safeProfiles.find((p) => p.jid === bare),
        platesClaimedByChats,
      })
      map.set(d.driverJid, { name, chatJid, plate })
    }
    return map
  }, [drivers, chats, profiles, accountChats, cars])
}

function DriverNameButton({
  name,
  plate,
  chatJid,
  onClick,
}: {
  name: string
  plate?: string
  chatJid?: string
  onClick: () => void
}) {
  // The plate sits outside the button: it identifies the truck, it is not
  // part of the link to the driver's chat.
  const badge = plate ? <LicensePlate plateNumber={plate} /> : null
  // Mock style: the name stays dark — the blue "N refuels" line below is
  // the visual link cue; clickable names just underline on hover.
  if (!chatJid)
    return (
      <span className="inline-flex items-center gap-2 font-medium">
        {badge}
        {name}
      </span>
    )
  return (
    <span className="inline-flex items-center gap-2 font-medium">
      {badge}
      <button onClick={onClick} className="text-left hover:underline">
        {name}
      </button>
    </span>
  )
}

export function FuelReport({
  onNavigateToChat,
}: {
  onNavigateToChat: (jid: string, tab?: TabType) => void
}) {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [range, setRange] = useState<FuelRange>(() => {
    const today = new Date()
    return { from: today, to: today }
  })
  const [selectedDriver, setSelectedDriver] = useState<string>("all")
  const [driverPickerOpen, setDriverPickerOpen] = useState(false)
  const [detail, setDetail] = useState<{
    driverJid: string
    driverName?: string
    driverPlate?: string
    eventKind: "alert" | "refuel"
    eventId: string
    at: string
    window: { from: string; to: string }
    partnerAt?: string
    suppressed?: boolean
    reason?: string | null
  } | null>(null)

  const { drivers, isLoading, error, refetch } = useFuelReportData(range)
  const nameMap = useDriverNameMap(drivers)

  // Dropdown options: every driver in the fetched data, sorted by display name.
  // If the currently selected driver has no data in the fetched range, keep
  // it in the list so the <select> value doesn't silently diverge from the
  // visible options.
  const driverOptions = useMemo(() => {
    const options = drivers.map((d) => ({
      jid: d.driverJid,
      name: nameMap.get(d.driverJid)?.name ?? d.driverJid,
      plate: nameMap.get(d.driverJid)?.plate,
    }))
    if (
      selectedDriver !== "all" &&
      !options.some((o) => o.jid === selectedDriver)
    ) {
      options.push({
        jid: selectedDriver,
        name:
          nameMap.get(selectedDriver)?.name ??
          formatJidLocalpartAsName(selectedDriver) ??
          selectedDriver,
        plate: nameMap.get(selectedDriver)?.plate,
      })
    }
    return options.sort((a, b) => a.name.localeCompare(b.name))
  }, [drivers, nameMap, selectedDriver])

  const selectedDriverOption = driverOptions.find(
    (driver) => driver.jid === selectedDriver,
  )

  const filtered = useMemo(
    () =>
      selectedDriver === "all"
        ? drivers
        : drivers.filter((d) => d.driverJid === selectedDriver),
    [drivers, selectedDriver],
  )
  const summary = useMemo(() => computeFuelSummary(filtered), [filtered])
  const multiDay =
    format(range.from, "yyyy-MM-dd") !== format(range.to, "yyyy-MM-dd")

  const handleNavigate = useCallback(
    (jid: string) => {
      dispatch(changeActiveChat({ activeChatJid: jid }))
      dispatch(changeActiveTab({ activeTab: TAB_TYPES.CHATS }))
      onNavigateToChat(jid, TAB_TYPES.CHATS)
    },
    [dispatch, onNavigateToChat],
  )

  const openDetail = useCallback(
    (
      row: DriverFuelRow,
      chip: ChipEvent,
      driverName?: string,
      driverPlate?: string,
    ) => {
      const r = chip.refuel
      const linkedAlert = r.linkedAlertId
        ? row.alerts.find((a) => a.id === r.linkedAlertId)
        : undefined
      setDetail({
        driverJid: row.driverJid,
        driverName,
        driverPlate,
        eventKind: "refuel",
        // Query by the linked alert when there is one — only the alert side
        // carries the "seen" status, and its payload includes this refuel.
        eventId: r.linkedAlertId ?? r.id,
        at: r.refueledAt,
        window: eventDayWindow(r.refueledAt),
        partnerAt: linkedAlert?.sentAt,
        // A refuel can be linked to a breach we deliberately did NOT send —
        // the dialog must say so instead of pretending an alert went out.
        suppressed: linkedAlert?.suppressed,
        reason: linkedAlert?.reason,
      })
    },
    [],
  )

  return (
    <>
      {createPortal(
        <div className="fixed inset-0 left-[49px] z-40 flex flex-col overflow-auto bg-muted/30">
          <div className="p-4">
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold">
                  {t("reports.fuelReport.title")}
                </h1>
                <span className="text-xs text-muted-foreground">
                  {t("reports.fuelReport.localTimezone")}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Popover
                  open={driverPickerOpen}
                  onOpenChange={setDriverPickerOpen}
                >
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-label={t("reports.fuelReport.filterByDriver")}
                      aria-expanded={driverPickerOpen}
                      className="w-[300px] justify-between font-normal"
                    >
                      <span className="flex min-w-0 items-center gap-2">
                        {selectedDriverOption?.plate && (
                          <LicensePlate
                            plateNumber={selectedDriverOption.plate}
                            className="shrink-0"
                          />
                        )}
                        <span className="truncate">
                          {selectedDriverOption?.name ??
                            t("reports.fuelReport.allDrivers")}
                        </span>
                      </span>
                      <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-[300px] p-0">
                    <Command>
                      <CommandInput
                        placeholder={t("reports.fuelReport.searchDrivers")}
                      />
                      <CommandList>
                        <CommandEmpty>
                          {t("reports.shared.noDriverFound")}
                        </CommandEmpty>
                        <CommandGroup>
                          <CommandItem
                            value={t("reports.fuelReport.allDrivers")}
                            onSelect={() => {
                              setSelectedDriver("all")
                              setDriverPickerOpen(false)
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                selectedDriver === "all"
                                  ? "opacity-100"
                                  : "opacity-0",
                              )}
                            />
                            {t("reports.fuelReport.allDrivers")}
                          </CommandItem>
                          {driverOptions.map((driver) => (
                            <CommandItem
                              key={driver.jid}
                              value={`${driver.plate ?? ""} ${driver.name}`}
                              onSelect={() => {
                                setSelectedDriver(driver.jid)
                                setDriverPickerOpen(false)
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  selectedDriver === driver.jid
                                    ? "opacity-100"
                                    : "opacity-0",
                                )}
                              />
                              <span className="flex min-w-0 items-center gap-2">
                                {driver.plate && (
                                  <LicensePlate
                                    plateNumber={driver.plate}
                                    className="shrink-0"
                                  />
                                )}
                                <span className="truncate">{driver.name}</span>
                              </span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                {/* DateRangePicker's maxDays is a from->to day difference, so
                    30 = 31 inclusive calendar days, matching the backend's
                    31-day (timedelta(days=31)) cap. */}
                <DateRangePicker
                  dateRange={range}
                  setDateRange={(r) => r && setRange(r)}
                  maxDays={30}
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={refetch}
                  disabled={isLoading}
                >
                  <RefreshCw
                    className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
                  />
                </Button>
              </div>
            </div>

            <FuelSummaryCard summary={summary} />

            <div className="mb-2 flex gap-4 text-xs text-muted-foreground">
              <span>
                <span className="mr-1 inline-block h-2 w-2 rounded-full bg-green-500" />
                {t("reports.fuelLegend.matched")}
              </span>
              <span>
                <span className="mr-1 inline-block h-2 w-2 rounded-full bg-red-500" />
                {t("reports.fuelLegend.elsewhere")}
              </span>
              <span>
                <span className="mr-1 inline-block h-2 w-2 rounded-full bg-gray-400" />
                {t("reports.fuelLegend.noAlert")}
              </span>
            </div>

            {isLoading && drivers.length === 0 && !error && (
              <div className="flex flex-col items-center gap-3 py-16 text-muted-foreground">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                <span className="text-sm font-medium">
                  {t("reports.fuelReport.loading")}
                </span>
              </div>
            )}
            {error && (
              <div className="py-12 text-center text-red-500">
                {t("reports.shared.error", { error })}
              </div>
            )}

            {!error && (drivers.length > 0 || !isLoading) && (
              // Keep the previous range's table (dimmed, with a loading badge
              // pinned to the viewport) while the next one loads — no
              // full-screen flash on every range change.
              <div className="relative">
                {isLoading && (
                  <div className="pointer-events-none absolute inset-0 z-10 rounded-xl bg-background/50">
                    <div className="sticky top-1/3 flex justify-center py-8">
                      <div className="flex items-center gap-3 rounded-lg border bg-background px-4 py-3 shadow-lg">
                        <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                        <span className="text-sm font-medium">
                          {t("reports.fuelReport.reloading")}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                <div
                  className={`rounded-xl border bg-background ${isLoading ? "pointer-events-none opacity-60" : ""}`}
                >
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[300px]">
                          {t("reports.fuelReport.columns.driver")}
                        </TableHead>
                        <TableHead>
                          {t("reports.fuelReport.columns.refuels")}
                        </TableHead>
                        <TableHead className="w-40">
                          {t("reports.fuelReport.columns.suggested")}
                        </TableHead>
                        <TableHead className="w-40">
                          {t("reports.fuelReport.columns.elsewhere")}
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filtered.map((row) => {
                        const resolved = nameMap.get(row.driverJid)
                        const chips = driverChipEvents(row)
                        const stats = driverStats(row)
                        return (
                          <TableRow key={row.driverJid}>
                            <TableCell className="py-4">
                              {/* Icon centered against the two-line name block. */}
                              <div className="flex items-center gap-2.5">
                                <UserRound className="h-4 w-4 shrink-0 text-muted-foreground" />
                                <div className="min-w-0 whitespace-nowrap">
                                  <DriverNameButton
                                    name={resolved?.name ?? row.driverJid}
                                    plate={resolved?.plate}
                                    chatJid={resolved?.chatJid}
                                    onClick={() =>
                                      resolved?.chatJid &&
                                      handleNavigate(resolved.chatJid)
                                    }
                                  />
                                  <div className="text-sm text-blue-600 dark:text-blue-400">
                                    {t("reports.fuelReport.refuelCount", {
                                      count: stats.refuels,
                                    })}
                                  </div>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="py-4">
                              {chips.length === 0 ? (
                                <span className="text-muted-foreground">—</span>
                              ) : (
                                <div className="flex flex-wrap gap-1.5">
                                  {chips.map((chip) => (
                                    <FuelTimelineChip
                                      key={chip.refuel.id}
                                      event={chip}
                                      showDate={multiDay}
                                      onClick={() =>
                                        openDetail(
                                          row,
                                          chip,
                                          resolved?.name,
                                          resolved?.plate,
                                        )
                                      }
                                    />
                                  ))}
                                </div>
                              )}
                            </TableCell>
                            <TableCell className="py-4 align-top">
                              <div className="font-medium">{stats.matched}</div>
                              <div
                                className={`text-xs ${stats.matched > 0 ? "text-green-600" : "text-muted-foreground"}`}
                              >
                                {formatPct(stats.matchedPct)}
                              </div>
                            </TableCell>
                            <TableCell className="py-4 align-top">
                              <div className="font-medium">
                                {stats.elsewhere}
                              </div>
                              <div
                                className={`text-xs ${stats.elsewhere > 0 ? "text-amber-500" : "text-muted-foreground"}`}
                              >
                                {formatPct(stats.elsewherePct)}
                              </div>
                            </TableCell>
                          </TableRow>
                        )
                      })}
                      {filtered.length === 0 && (
                        <TableRow>
                          <TableCell
                            colSpan={4}
                            className="py-8 text-center text-muted-foreground"
                          >
                            {t("reports.fuelReport.empty")}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>
        </div>,
        document.body,
      )}

      {detail && (
        <FuelEventDetailDialog
          open
          driverJid={detail.driverJid}
          driverName={detail.driverName}
          driverPlate={detail.driverPlate}
          eventKind={detail.eventKind}
          eventId={detail.eventId}
          eventAt={detail.at}
          window={detail.window}
          partnerAt={detail.partnerAt}
          suppressed={detail.suppressed}
          reason={detail.reason}
          onOpenChange={(o) => !o && setDetail(null)}
        />
      )}
    </>
  )
}
